import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Box, Typography, Stack, Divider } from '@mui/material'
import {
  CommonButton,
  CommonSelect,
  CommonDatePicker,
  CommonRadio,
} from '@/components/common'
import {
  ResultCard,
  DonorPortalScreenMessage,
  SuggestionsSection,
  InfiniteScrollSentinel,
  CityAutocompleteField,
} from '@/components/donation'
import {
  useGetProcedureTypes,
  useGetCitySearch,
  useInfiniteScrollResults,
} from '@/hooks'
import { useDonorContext } from '@/context'
import { ROUTES, DONOR_PORTAL_SCREEN_CONFIG_CODE } from '@/constants'
import { ResultCardSkeleton } from '@/pages/donate-near-you/results/Skeletons'
import {
  buildResultItem,
  formatIsoDateWithWeekday,
  getDonorEligibilityForScheduleRow,
  getScheduledDonationsParams,
  getSearchByCityParams,
  isScheduleRowBookable,
  isValidCity,
  isDateRangeSearchValid,
  isOpenEndedDateRangeSearch,
  parseSearchByCityParams,
  CITY_ERROR_MESSAGE,
} from '@/utils'

const YES_NO_ITEMS = [
  { id: 'true', label: 'Yes' },
  { id: 'false', label: 'No' },
]

function isoYmdToLocalDate(iso) {
  if (!iso || typeof iso !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(iso))
    return null
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d)
}

function canFetchCitySearch(fd) {
  if (!fd?.city?.trim() || !isValidCity(fd.city)) return false
  if (!isDateRangeSearchValid(fd.start_date, fd.end_date)) return false
  if (!fd.include_mobile_drives && !fd.include_donor_centers) return false
  return true
}

export const SearchByCityResults = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { procedureTypes, loadingProcedureTypes, getProcedureTypes } =
    useGetProcedureTypes()
  const {
    citySearchResults,
    citySearchSuggestions,
    getCitySearch,
    loadingCitySearch,
    clearCitySearch,
  } = useGetCitySearch()
  const { donor, getDonor } = useDonorContext()

  const [formData, setFormData] = useState({})
  const [cityTouched, setCityTouched] = useState(false)
  const [cityFocused, setCityFocused] = useState(false)
  const cityInputRef = useRef(null)

  useEffect(() => {
    getProcedureTypes()
  }, [])

  useEffect(() => {
    getDonor()
  }, [getDonor])

  const searchQuery = searchParams.toString()

  useEffect(() => {
    const parsed = parseSearchByCityParams(searchParams)
    const fd = {
      ...parsed,
      start_date: isoYmdToLocalDate(parsed.start_date),
      end_date: isoYmdToLocalDate(parsed.end_date),
    }
    setFormData(fd)
    if (canFetchCitySearch(fd)) getCitySearch(fd)
    else clearCitySearch()
  }, [searchQuery, searchParams, getCitySearch, clearCitySearch])

  const handleFormDataChange = useCallback(
    (key, value) => {
      setFormData(prev => {
        const updated = { ...prev, [key]: value }
        if (!canFetchCitySearch(updated)) clearCitySearch()
        return updated
      })
    },
    [clearCitySearch]
  )

  const runSearchFromFilters = useCallback(() => {
    setCityTouched(true)
    if (!canFetchCitySearch(formData)) {
      if (formData.city?.trim() && !isValidCity(formData.city)) {
        cityInputRef.current?.blur?.()
        document.getElementById('sbc-res-city')?.blur?.()
      }
      return
    }
    getCitySearch(formData)
    const params = getSearchByCityParams(formData)
    navigate(`${ROUTES.DONATE_CITY_RESULTS}?${params.toString()}`)
  }, [formData, navigate, getCitySearch])

  const showCityError =
    cityTouched &&
    !cityFocused &&
    !!formData.city?.trim() &&
    !isValidCity(formData.city)
  const cityErrorMessage = showCityError ? CITY_ERROR_MESSAGE : ''

  const filterSearchDisabled = useMemo(() => {
    if (!formData.city?.trim() || !isValidCity(formData.city)) return true
    if (!isDateRangeSearchValid(formData.start_date, formData.end_date))
      return true
    if (!formData.include_mobile_drives && !formData.include_donor_centers)
      return true
    return false
  }, [formData])

  const builtResults = useMemo(() => {
    if (!citySearchResults?.length) return []
    return citySearchResults.map(row => buildResultItem(row, {}))
  }, [citySearchResults])

  const useInfiniteScroll = isOpenEndedDateRangeSearch(
    formData.start_date,
    formData.end_date
  )

  const {
    visibleItems: visibleBuiltResults,
    hasMore: hasMoreResults,
    loadingMore: loadingMoreResults,
    sentinelRef: resultsSentinelRef,
  } = useInfiniteScrollResults(builtResults, 20, useInfiniteScroll)

  const visibleRawResults = useMemo(() => {
    if (!citySearchResults?.length) return []
    return citySearchResults.slice(0, visibleBuiltResults.length)
  }, [citySearchResults, visibleBuiltResults.length])

  const builtSuggestions = useMemo(() => {
    if (!citySearchSuggestions?.length) return []
    return citySearchSuggestions.map(row => buildResultItem(row, {}))
  }, [citySearchSuggestions])

  const handleSelectResult = useCallback(
    (resultItem, rawRow) => {
      if (!isScheduleRowBookable(rawRow)) return
      const { applies, selectable } = getDonorEligibilityForScheduleRow({
        donorId: donor?.id,
        procedureEligibilities: donor?.procedure_eligibilities,
        selectedProcedureTypeId: formData.procedure_type_id,
        appointmentDayIso: resultItem.date,
        procedureTypesOnRow: rawRow?.procedure_types,
      })
      if (applies && !selectable) return
      navigate(
        `${ROUTES.SCHEDULE_DONATION}?${getScheduledDonationsParams({
          ...resultItem,
          ...formData,
          date: resultItem.date,
        }).toString()}`
      )
    },
    [donor?.id, donor?.procedure_eligibilities, formData, navigate]
  )

  const renderScheduleResultCard = useCallback(
    (resultItem, rawRow, index) => {
      const elig = getDonorEligibilityForScheduleRow({
        donorId: donor?.id,
        procedureEligibilities: donor?.procedure_eligibilities,
        selectedProcedureTypeId: formData.procedure_type_id,
        appointmentDayIso: resultItem.date,
        procedureTypesOnRow: rawRow?.procedure_types,
      })
      const scheduleBlocked =
        !isScheduleRowBookable(rawRow) || (elig.applies && !elig.selectable)

      return (
        <ResultCard
          key={`${resultItem.operation_id}-${resultItem.date}-${index}`}
          item={resultItem}
          operationDate={
            resultItem.date
              ? formatIsoDateWithWeekday(resultItem.date)
              : undefined
          }
          onSelect={() => handleSelectResult(resultItem, rawRow)}
          isSelectDisabled={scheduleBlocked}
          selectionSurface='buttonOnly'
          eligibilityChip={elig.applies ? elig.chip : null}
        />
      )
    },
    [
      donor?.id,
      donor?.procedure_eligibilities,
      formData.procedure_type_id,
      handleSelectResult,
    ]
  )

  return (
    <Stack spacing={2} sx={{ pb: 1 }}>
      <DonorPortalScreenMessage
        code={DONOR_PORTAL_SCREEN_CONFIG_CODE.SEARCH_BY_CITY_RESULTS}
      />
      <Typography variant='h5' component='h1' fontWeight={600}>
        Search by City
      </Typography>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          sx={{ alignItems: { md: 'flex-start' }, flexWrap: 'wrap' }}
        >
          <Box sx={{ flex: { md: '1 1 140px' }, minWidth: 140 }}>
            <CityAutocompleteField
              id='sbc-res-city'
              value={formData.city ?? ''}
              onChange={city => handleFormDataChange('city', city)}
              error={!!cityErrorMessage}
              helperText={cityErrorMessage}
              inputRef={cityInputRef}
              onFocus={() => setCityFocused(true)}
              onBlur={() => {
                setCityFocused(false)
                setCityTouched(true)
              }}
            />
          </Box>
          <Box sx={{ flex: { md: '1 1 140px' }, minWidth: 140 }}>
            <CommonDatePicker
              id='sbc-res-start'
              label='Start date'
              value={formData.start_date ?? null}
              onChange={d => handleFormDataChange('start_date', d)}
              disablePast
              required
            />
          </Box>
          <Box sx={{ flex: { md: '1 1 140px' }, minWidth: 140 }}>
            <CommonDatePicker
              id='sbc-res-end'
              label='End date'
              value={formData.end_date ?? null}
              onChange={d => handleFormDataChange('end_date', d)}
              disablePast
              helperText='Leave blank to show all future dates'
            />
          </Box>
          <Box sx={{ flex: { md: '2 1 200px' }, minWidth: 180 }}>
            <CommonSelect
              id='sbc-res-procedure'
              label='Donation type'
              placeholder='All Available'
              options={procedureTypes}
              loading={loadingProcedureTypes}
              value={formData.procedure_type_id ?? ''}
              onChange={v => handleFormDataChange('procedure_type_id', v || '')}
            />
          </Box>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ alignItems: { sm: 'center' } }}
        >
          <Box
            sx={{
              '& .MuiFormControlLabel-root': { mr: 2.5 },
            }}
          >
            <CommonRadio
              id='sbc-res-include-drives'
              label='Mobile drives'
              row
              value={formData.include_mobile_drives ? 'true' : 'false'}
              setValue={v =>
                handleFormDataChange('include_mobile_drives', v === 'true')
              }
              items={YES_NO_ITEMS}
            />
          </Box>
          <Box
            sx={{
              '& .MuiFormControlLabel-root': { mr: 2.5 },
            }}
          >
            <CommonRadio
              id='sbc-res-include-centers'
              label='Donor centers'
              row
              value={formData.include_donor_centers ? 'true' : 'false'}
              setValue={v =>
                handleFormDataChange('include_donor_centers', v === 'true')
              }
              items={YES_NO_ITEMS}
            />
          </Box>
          <Box sx={{ flex: 1 }} />
          <CommonButton
            id='sbc-res-search'
            label='Search'
            variant='primary'
            disabled={filterSearchDisabled}
            onClick={runSearchFromFilters}
          />
        </Stack>
      </Stack>

      <Divider />

      <Box sx={{ mt: 2 }}>
        <SuggestionsSection
          loading={loadingCitySearch}
          hasResults={visibleBuiltResults.length > 0}
          hasSuggestions={builtSuggestions.length > 0}
          suggestionsSubtitle={
            formData.city
              ? `Here are upcoming sessions near ${formData.city} on other dates`
              : undefined
          }
        >
          <Stack spacing={2}>
            {builtSuggestions.map((resultItem, index) =>
              renderScheduleResultCard(
                resultItem,
                citySearchSuggestions[index],
                index
              )
            )}
          </Stack>
        </SuggestionsSection>
        <Stack spacing={2}>
          {loadingCitySearch
            ? Array.from({ length: 5 }).map((_, index) => (
                <ResultCardSkeleton key={`skeleton-${index}`} />
              ))
            : visibleBuiltResults.map((resultItem, index) =>
                renderScheduleResultCard(
                  resultItem,
                  visibleRawResults[index],
                  index
                )
              )}
          <InfiniteScrollSentinel
            sentinelRef={resultsSentinelRef}
            loadingMore={loadingMoreResults}
            hasMore={useInfiniteScroll && hasMoreResults}
          />
        </Stack>
      </Box>
    </Stack>
  )
}

export default SearchByCityResults
